/**
 * Account-Only Registration Service
 *
 * Creates Firebase Auth user and minimal Firestore profile WITHOUT team creation.
 * Used for email/password signup to align with Contract v1.0.0 §1 happy path:
 * R_SIGNUP (account) → R_NEW_TEAM (create team) → R_VERIFY_NOTICE → verify → onboarding
 *
 * Team creation happens separately at /onboarding/create-team for ALL new users.
 */

import {
  createUserWithEmailAndPassword,
  updateProfile,
  deleteUser,
  type User,
} from 'firebase/auth';
import { doc, serverTimestamp, writeBatch } from 'firebase/firestore';
import { auth, db } from '@/lib/firebase';
import { logger } from '@/lib/logger';

export interface AccountOnlyRegistrationInput {
  // Account - split name fields
  firstName: string;
  lastName: string;
  fullName: string;
  email: string;
  password: string;

  // Acquisition tracking (required)
  referralSource: string;
  // Only required when referralSource === 'other'
  referralOther?: string;
}

export interface AccountOnlyRegistrationResult {
  success: boolean;
  userId?: string;
  error?: string;
  errorCode?: string;
}

/**
 * Create Firebase Auth user and minimal profile WITHOUT team creation.
 *
 * Flow:
 * 1. Create Firebase Auth user
 * 2. Update user profile with display name
 * 3. Create minimal Firestore documents:
 *    - /users/{uid} (user profile, NO activeTeamId)
 *    - /users/{uid}/profile/data (name fields)
 * 4. If Firestore fails, delete the Auth user (rollback)
 *
 * Does NOT:
 * - Create team
 * - Create membership
 * - Set activeTeamId
 * - Send verification email (happens after team creation per §8.1)
 *
 * @param input - Registration data (account only)
 * @returns Result with success status, userId, or error details
 */
export async function accountOnlyRegistration(
  input: AccountOnlyRegistrationInput
): Promise<AccountOnlyRegistrationResult> {
  let authUser: User | null = null;

  try {
    // Step 1: Create Firebase Auth user
    logger.info('[AccountOnlyRegistration] Creating Auth user...');
    const userCredential = await createUserWithEmailAndPassword(auth, input.email, input.password);
    authUser = userCredential.user;

    // Step 2: Update profile with display name
    await updateProfile(authUser, { displayName: input.fullName });
    logger.info('[AccountOnlyRegistration] Auth user created:', authUser.uid);

    // Step 3: Create minimal Firestore documents (NO team)
    logger.info('[AccountOnlyRegistration] Creating user profile (no team)...');
    const batch = writeBatch(db);

    // 3a. Create user profile WITHOUT activeTeamId
    // User will get activeTeamId after creating team at /onboarding/create-team
    const userRef = doc(db, 'users', authUser.uid);
    batch.set(
      userRef,
      {
        email: input.email,
        displayName: input.fullName, // Legacy field for backward compatibility
        // NO activeTeamId - will be set when team is created
        // NO initialTeamId - not needed for account-only registration
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      },
      { merge: true }
    );

    // 3b. Create user profile/data subcollection (source of truth for names)
    const profileDataRef = doc(db, 'users', authUser.uid, 'profile', 'data');
    batch.set(profileDataRef, {
      firstName: input.firstName,
      lastName: input.lastName,
      fullName: input.fullName,
      // Store acquisition tracking on user profile since no team yet
      referralSource: input.referralSource,
      referralSourceNote: input.referralOther || null,
      updatedAt: serverTimestamp(),
    });

    // Commit user profile
    await batch.commit();

    logger.info('[AccountOnlyRegistration] Successfully created account (no team):', {
      userId: authUser.uid,
      email: input.email,
    });

    // NOTE: Verification email is NOT sent here
    // Per Contract §8.1, it's sent after team creation at /onboarding/create-team

    return {
      success: true,
      userId: authUser.uid,
    };
  } catch (error) {
    logger.error('[AccountOnlyRegistration] Registration failed:', error);

    // Determine error phase
    const isAuthError = !authUser;
    const phase = isAuthError ? 'auth' : 'firestore';

    // Step 4: Rollback - delete Auth user if Firestore failed
    if (authUser) {
      logger.info('[AccountOnlyRegistration] Rolling back Auth user...');
      try {
        await deleteUser(authUser);
        logger.info('[AccountOnlyRegistration] Auth user deleted (rollback successful)');
      } catch (rollbackError) {
        logger.error(
          '[AccountOnlyRegistration] CRITICAL: Failed to delete Auth user:',
          rollbackError
        );
        return {
          success: false,
          error: 'errors.registration.rollbackFailed',
          errorCode: 'ROLLBACK_FAILED',
        };
      }
    }

    // Map Firebase Auth errors to i18n translation keys
    let errorMessageKey = 'errors.registration.unknown';
    let errorCode = 'UNKNOWN';

    if (error && typeof error === 'object' && 'code' in error) {
      const code = error.code as string;
      errorCode = code;

      switch (code) {
        case 'auth/email-already-in-use':
          errorMessageKey = 'errors.registration.emailAlreadyInUse';
          break;
        case 'auth/weak-password':
          errorMessageKey = 'errors.registration.weakPassword';
          break;
        case 'auth/invalid-email':
          errorMessageKey = 'errors.registration.invalidEmail';
          break;
        case 'auth/operation-not-allowed':
          errorMessageKey = 'errors.registration.operationNotAllowed';
          break;
        case 'auth/network-request-failed':
          errorMessageKey = 'errors.registration.networkError';
          break;
        case 'permission-denied':
          errorMessageKey = 'errors.registration.permissionDenied';
          break;
        default:
          if (code.startsWith('auth/')) {
            errorMessageKey = 'errors.registration.authError';
          }
      }
    }

    return {
      success: false,
      error: errorMessageKey,
      errorCode,
    };
  }
}
